import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatNzDate, mailboxAddress, nzDateTimeParts } from '@promanage/core';
import { Button, Chip, Field, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { LoadFailed } from '../components/QueryState';
import { CommunicationsNav } from '../features/lists/CommunicationsNav';
import { useSession } from '../features/auth/session';
import { MessageBody, isEmbeddedImage } from '../features/inbox/MessageBody';
import { ReplyComposer, ReplyThread } from '../features/inbox/Replies';
import { fileSize } from '../features/reports/delivery';
import { documentBlob } from '../features/reports/downloadDocument';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/communications/inbox/$id')({
  component: MessagePage,
});

type Message = RouterOutputs['mailInbox']['get'];

const when = (at: string) => {
  const parts = nzDateTimeParts(new Date(at));
  return `${formatNzDate(parts.date)} ${parts.time}`;
};

/** How the message came to be whose it is — said plainly, because "why did I get this" is asked. */
const ROUTED: Record<Message['routedBy'], string> = {
  token: 'A reply to an email this agency sent',
  sender: 'Matched to the sender’s property manager',
  none: 'Not matched to anybody',
};

/**
 * One received email, whole.
 *
 * ── TEXT, NEVER MARKUP ───────────────────────────────────────────────────────────────────────
 *
 * The body is shown as the words it contained. Nothing a sender wrote is rendered as HTML, so no
 * link is dressed as another, nothing runs, and nothing is fetched from anybody. That costs
 * formatting and buys the one property an inbox open to the internet has to have.
 *
 * AN IMAGE THE MESSAGE EMBEDDED IS SHOWN WHERE IT SAT (0149) — from this agency's own stored copy
 * of the attachment, which the upload gate sniffed as a raster image. See `MessageBody`.
 *
 * ── OPENING IT IS READING IT ─────────────────────────────────────────────────────────────────
 *
 * The server marks a message read for the person who asked for it. "Mark unread" puts it back on
 * that person's own list and nobody else's.
 */
function MessagePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();

  const message = useQuery({
    ...trpc.mailInbox.get.queryOptions({ id }, { staleTime: 30_000 }),
    meta: { errorTitle: 'Could not load that message' },
    // A reply is sent by the worker, usually within seconds of Send being pressed. While one is
    // on its way the page asks again, so "Sending" becomes "Sent" without a reload — and stops
    // asking once nothing is waiting.
    refetchInterval: (query) =>
      query.state.data?.replies.some((reply) => reply.state === 'queued') ? 4_000 : false,
  });
  const staff = useQuery(
    trpc.mailInbox.assignees.queryOptions(undefined, { staleTime: 5 * 60_000 }),
  );

  /*
   * OPENING A MESSAGE READS IT — and reads the bell's "this was assigned to you" with it. Both
   * happen on the server as the message is fetched, so the numbers on the rail are out of date
   * the moment it arrives, and are asked for again rather than left wrong for up to a minute.
   */
  const opened = message.data?.id;
  useEffect(() => {
    if (opened === undefined) return;
    void queryClient.invalidateQueries({ queryKey: trpc.alerts.badges.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.alerts.list.queryKey() });
  }, [opened, queryClient]);

  // The list and the tab's count both change when a message is read, unread or handed over.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.mailInbox.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.mailInbox.counts.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.alerts.badges.queryKey() }),
    ]);

  const assign = useMutation(
    trpc.mailInbox.assign.mutationOptions({
      meta: { errorTitle: 'Could not assign that message' },
      async onSuccess() {
        await Promise.all([
          refresh(),
          queryClient.invalidateQueries({ queryKey: trpc.mailInbox.get.queryKey({ id }) }),
        ]);
      },
    }),
  );
  const setDeleted = useMutation(
    trpc.mailInbox.setDeleted.mutationOptions({
      meta: { errorTitle: 'Could not change that message' },
      async onSuccess(result) {
        await refresh();
        if (result.deleted) {
          // Gone from the inbox, so back to it. It is under Deleted, and can be restored.
          void navigate({ to: '/communications/inbox' });
        } else {
          await queryClient.invalidateQueries({ queryKey: trpc.mailInbox.get.queryKey({ id }) });
        }
      },
    }),
  );
  const setRead = useMutation(
    trpc.mailInbox.setRead.mutationOptions({
      meta: { errorTitle: 'Could not mark that message unread' },
      async onSuccess() {
        await refresh();
        // Back to the list, where it is unread again — staying here would only re-read it.
        void navigate({ to: '/communications/inbox' });
      },
    }),
  );

  if (message.error) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Inbox" subtitle="Not found" />
        <CommunicationsNav current="inbox" />
        <LoadFailed retry={() => void message.refetch()} />
      </div>
    );
  }
  if (!message.data) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Inbox" subtitle="Loading…" />
        <CommunicationsNav current="inbox" />
        <Panel className="h-40" />
      </div>
    );
  }

  const data = message.data;
  const sender = data.personName ?? data.fromName ?? data.fromAddress;
  const mine = data.assignedUserId === session?.user.id;
  // An image the message embedded is shown in its words (0149); every other file is offered.
  const embedded = data.attachments.filter(isEmbeddedImage);
  const offered = data.attachments.filter((file) => !isEmbeddedImage(file));

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={data.subject ?? '(no subject)'}
        subtitle={`From ${sender} · ${when(data.receivedAt)}`}
        meta={
          data.assignedUserName ? (
            <Chip tone="neutral">{mine ? 'Assigned to you' : data.assignedUserName}</Chip>
          ) : (
            <Chip tone="warn">Unassigned</Chip>
          )
        }
        actions={
          <>
            {data.deletedAt === null ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={setRead.isPending}
                  onClick={() => setRead.mutate({ id, read: false })}
                >
                  Mark unread
                </Button>
                {/*
                 * NO "ARE YOU SURE". It is undone with one press for a year — the message goes to
                 * Deleted, not away — and a confirmation on a reversible act only teaches people
                 * to click through the ones that matter.
                 */}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={setDeleted.isPending}
                  onClick={() => setDeleted.mutate({ id, deleted: true })}
                >
                  Delete
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                disabled={setDeleted.isPending}
                onClick={() => setDeleted.mutate({ id, deleted: false })}
              >
                Restore
              </Button>
            )}
            <Link to="/communications/inbox">
              <Button size="sm" variant="secondary">
                Back to inbox
              </Button>
            </Link>
          </>
        }
      />

      <CommunicationsNav current="inbox" />

      <div className="grid items-start gap-4 lg:grid-cols-3">
        <Panel className="p-5 lg:col-span-2">
          {/*
           * WHO, TO WHOM, WHEN — above the words, where a letter has them. Read before the message
           * rather than hunted for in a panel beside it (which on a phone is below everything).
           * The address is always shown beside the name: a display name is whatever the sender
           * typed, and the address is the part that says who it came from.
           */}
          <dl className="mb-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 border-b border-rule pb-4 text-[0.8125rem]">
            <dt className="text-ink-muted">From</dt>
            <dd className="min-w-0">
              {data.fromName ? <span className="font-semibold">{data.fromName} </span> : null}
              <span className={`break-all ${data.fromName ? 'text-ink-muted' : 'font-semibold'}`}>
                {data.fromAddress}
              </span>
            </dd>
            <dt className="text-ink-muted">To</dt>
            {/* The agency's own address as it stood the day this arrived. */}
            <dd className="min-w-0 break-all">{mailboxAddress(data.localPart, 'mail')}</dd>
            <dt className="text-ink-muted">Received</dt>
            <dd className="min-w-0 tabular-nums">{when(data.receivedAt)}</dd>
          </dl>

          {data.deletedAt !== null ? (
            /*
             * WHAT DELETED MEANS, WHERE IT IS READ. The message is still here and Restore brings it
             * back; what is on a clock is its files, and the day they go is said as a date.
             */
            <p className="mb-4 rounded-md bg-surface-sunk px-3 py-2 text-[0.8125rem] text-ink-muted">
              Deleted{data.deletedByName ? ` by ${data.deletedByName}` : ''} on{' '}
              {when(data.deletedAt)}. Restore brings it back to the inbox
              {data.attachments.length > 0 && data.attachmentsRemovedOn
                ? `. Its ${data.attachments.length === 1 ? 'attachment is' : 'attachments are'} removed for good on ${formatNzDate(data.attachmentsRemovedOn)}`
                : ''}
              .
            </p>
          ) : null}

          {!data.senderVerified ? (
            /*
             * SAID ABOVE THE WORDS, NOT BESIDE THEM. Anybody can type a From address; this one
             * failed the check its own domain asks receivers to make, or carried none. It is the
             * message most likely to be asking for a bank account to be changed.
             */
            <p className="mb-4 rounded-md bg-warn-soft px-3 py-2 text-[0.8125rem] text-warn">
              The sender could not be verified. This may not be from {data.fromAddress} — confirm
              anything it asks for by phone, on a number you already hold.
            </p>
          ) : null}

          {/*
           * THE SENDER'S WORDS, SET APART FROM OURS. Everything else in this panel is ProManage
           * talking — who it is from, a warning, the files. A sunk, ruled block marks where the
           * message itself starts and stops, so a line in it cannot pass for one of ours.
           */}
          <section aria-labelledby="message-body-heading">
            <h3 id="message-body-heading" className="text-[0.75rem] font-semibold text-ink-muted">
              Message
            </h3>
            <div className="mt-2 rounded-md bg-surface-sunk px-4 py-3.5 ring-1 ring-rule">
              <MessageBody body={data.body} images={embedded} />
            </div>
          </section>

          {offered.length > 0 ? (
            <div className="mt-5 border-t border-rule pt-4">
              <h3 className="text-[0.75rem] font-semibold text-ink-muted">
                {offered.length === 1 ? 'Attachment' : 'Attachments'}
              </h3>
              <ul className="mt-2 flex flex-col gap-2">
                {offered.map((file) => (
                  <AttachmentRow key={file.id} file={file} />
                ))}
              </ul>
            </div>
          ) : null}

          {data.skipped.length > 0 ? (
            <div className="mt-5 border-t border-rule pt-4">
              <h3 className="text-[0.75rem] font-semibold text-ink-muted">
                {data.skipped.length === 1 ? 'Attachment not kept' : 'Attachments not kept'}
              </h3>
              <ul className="mt-2 flex flex-col gap-1 text-[0.8125rem]">
                {data.skipped.map((file, index) => (
                  <li key={`${file.fileName}-${String(index)}`} className="min-w-0">
                    <span className="font-medium">{file.fileName}</span>
                    <span className="text-ink-muted"> — {file.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* What the agency wrote back, then the box to write more — in the order it is read. */}
          <ReplyThread replies={data.replies} />
          <ReplyComposer message={data} />
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel className="p-5">
            <PanelHeader title="Assigned to" className="p-0 pb-3" />
            <Field label="Person" markOptional={false} hint={ROUTED[data.routedBy]}>
              {(props) => (
                <Select
                  {...props}
                  value={data.assignedUserId ?? ''}
                  disabled={assign.isPending || staff.isPending}
                  onChange={(event) =>
                    assign.mutate({ id, assignedUserId: event.target.value || null })
                  }
                >
                  <option value="">Unassigned</option>
                  {(staff.data ?? []).map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.name}
                    </option>
                  ))}
                  {/* Somebody who has since left still shows as the holder until it is moved. */}
                  {data.assignedUserId &&
                  !(staff.data ?? []).some((p) => p.userId === data.assignedUserId) ? (
                    <option value={data.assignedUserId}>
                      {data.assignedUserName ?? 'Former staff'}
                    </option>
                  ) : null}
                </Select>
              )}
            </Field>
            {data.assignedAt ? (
              <p className="mt-2 text-[0.75rem] text-ink-muted">
                {data.assignedByName ? `By ${data.assignedByName}` : 'Automatically'} ·{' '}
                {when(data.assignedAt)}
              </p>
            ) : null}
          </Panel>

          {/*
           * What ProManage matched the message to. From, to and when are above the words; with
           * nothing matched there is nothing left to say here, so there is no panel.
           */}
          {data.personRef || data.record ? (
            <Panel className="p-5">
              <PanelHeader title="Matched to" className="p-0 pb-3" />
              <dl className="flex flex-col gap-3 text-[0.8125rem]">
                {data.personRef ? (
                  <Detail label="Contact">
                    <Link
                      to="/people/$ref"
                      params={{ ref: data.personRef }}
                      className="text-accent hover:underline"
                    >
                      {data.personName ?? data.personRef}
                    </Link>
                  </Detail>
                ) : null}
                {data.record ? (
                  <Detail label="About">
                    {data.record.type === 'tenancy' ? (
                      <Link
                        to="/tenancies/$ref"
                        params={{ ref: data.record.ref }}
                        className="text-accent hover:underline"
                      >
                        {data.record.label}
                      </Link>
                    ) : data.record.type === 'owner' ? (
                      <Link
                        to="/owners/$ref"
                        params={{ ref: data.record.ref }}
                        className="text-accent hover:underline"
                      >
                        {data.record.label}
                      </Link>
                    ) : (
                      <Link
                        to="/properties/$ref"
                        params={{ ref: data.record.ref }}
                        className="text-accent hover:underline"
                      >
                        {data.record.label}
                      </Link>
                    )}
                  </Detail>
                ) : null}
              </dl>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * One kept file, saved on the click.
 *
 * DOWNLOADED, NEVER OPENED IN THE PAGE — the one exception is an image the message embedded, which
 * `MessageBody` draws in place. It came from outside the agency. The upload gate has
 * established what kind of file it is — a PDF, an image, a document — and not that it is harmless,
 * so it goes to the operator's own machine, where their own software and its protections open it.
 *
 * Fetched when asked for, not with the message: a tenant's six photographs are tens of megabytes
 * nobody may ever look at.
 */
function AttachmentRow({ file }: { file: Message['attachments'][number] }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'fetching' | 'failed'>('idle');

  const save = async () => {
    setState('fetching');
    try {
      const fetched = await queryClient.fetchQuery(
        trpc.mailInbox.attachment.queryOptions({ attachmentId: file.id }, { staleTime: 0 }),
      );
      const url = URL.createObjectURL(documentBlob(fetched));
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = fetched.fileName;
      anchor.click();
      // The click has taken its own reference; a page left open should not hold the bytes.
      URL.revokeObjectURL(url);
      setState('idle');
    } catch {
      // Said on the row. A button that silently does nothing is one that gets pressed again.
      setState('failed');
    }
  };

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-surface-sunk px-3 py-2">
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[0.8125rem] font-medium">{file.fileName}</span>
        <span className="block text-[0.75rem] text-ink-muted">
          {fileSize(file.byteSize)}
          {state === 'failed' ? (
            <span className="text-danger"> · Could not be downloaded — try again</span>
          ) : null}
        </span>
      </div>
      {file.deletedAt === null ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={state === 'fetching'}
          onClick={() => void save()}
        >
          {state === 'fetching' ? 'Downloading' : 'Download'}
        </Button>
      ) : (
        // Its email is deleted. Restoring the email is how the file is read again.
        <span className="text-[0.75rem] text-ink-muted">Restore to download</span>
      )}
    </li>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.75rem] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 min-w-0">{children}</dd>
    </div>
  );
}
